import { useState, useEffect, useContext, useRef } from 'react';
import { AttachFile, Check } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButton,
    SttTable,
    SttTopControls,
} from '../../../sporttia/all';
import { useCrud, useInteractionsFiles } from '../../../lib/hooks';
import ModalFile from './ModalFile';
import DateBuilder from '../../../lib/DateBuilder';

type Poster = {
    id: string | number;
};

type FileItem = {
    id?: string | number;
    name: string;
    description?: string;
    public?: boolean;
    format?: string;
    created?: string;
};

type FilesData = {
    count: number;
    rows: FileItem[];
};

type Event = {
    id: string | number;
    poster?: Poster;
};

type Props = {
    event: Event;
};

export default function EventDocuments({ event }: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { uploadFile, downloadFile } = useInteractionsFiles();
    const inputFile = useRef<HTMLInputElement>(null);
    const [_uploading, setUploading] = useState<boolean>(false);
    const [_event, setEvent] = useState<Event>(event);
    const [files, setFiles, , Get, _Post, Put, Delete] = useCrud<FilesData>();
    const [file, setFile] = useState<FileItem | undefined>();
    const [forceUpdate, setforceUpdate] = useState<boolean>(false);

    useEffect(() => {
        console.log(_event.poster);
    }, [_event]);

    function loadFiles(): void {
        Get(`/events/${_event.id}/files`)
            .then((response: unknown) => {
                const data = response as FilesData;
                if (data.rows) {
                    setFiles(data);
                }
            })
            .catch(console.log);
    }

    function openSelectFile(): void {
        inputFile.current?.click();
    }

    function onFileSelected(files: FileList | null): void {
        if (files && files.length > 0) {
            const file = files[0]!;

            setUploading(true);

            // V7 API expects 'name' field and 'idEvent' (format is extracted from file.mimetype on backend)
            uploadFile(
                {
                    name: file.name,
                    type: 'EVENT_DOC',
                    idEvent: _event.id,
                },
                file,
            )
                .then(() => {
                    setUploading(false);
                    cxt?.showMessage('S', t('Uploaded'));
                    loadFiles();
                })
                .catch(console.log);
        }
    }

    function saveFile(file: FileItem): void {
        const { name, description } = file;
        const isPublic = file.public;

        Put(`/files/${file.id}`, { name, description, public: isPublic })
            .then((response: unknown) => {
                const data = response as { file?: FileItem };
                if (data.file) {
                    setFiles({
                        ...files,
                        rows: files.rows.map((f: any) =>
                            f.id === file.id ? data.file! : f,
                        ),
                    });
                    setFile(undefined);
                }
            })
            .catch(console.log);
    }

    function deleteFile(file: FileItem): void {
        Delete(`/files/${file.id}`).then((_response: unknown) => {
            setFiles({
                count: files.count - 1,
                rows: files.rows.filter((f: any) => f.id !== file.id),
            });
            setFile(undefined);
        });
    }

    function downloadFiles(file: FileItem): void {
        const parts = file.name.split('.');
        const extension = parts[parts.length - 1];
        let downloadPath = '';

        if (
            extension === 'jpg' ||
            extension === 'png' ||
            extension === 'jpeg'
        ) {
            downloadPath = '/images/';
        } else if (extension === 'pdf') {
            downloadPath = '/files/';
        }

        downloadFile(
            `${downloadPath}${file.id}.${extension}`,
            file.format,
            {},
            file.name,
        );
    }

    function setPoster(file: FileItem): void {
        Put(`/events/${_event.id}`, { idPoster: file.id })
            .then((response: unknown) => {
                const data = response as { event?: Event };
                if (data.event) {
                    setEvent(data.event);
                    setforceUpdate(!forceUpdate);
                }
            })
            .catch(console.log);
    }

    return (
        <>
            <input
                type="file"
                id="file"
                ref={inputFile}
                style={{ display: 'none' }}
                onChange={(event: any) => onFileSelected(event.target.files)}
            />
            <SttTopControls
                mainAction={{
                    type: 'component',
                    component: (
                        <SttButton
                            caption={t('Add')}
                            variant="contained"
                            color="primary"
                            startIcon={<AttachFile />}
                            onClick={openSelectFile}
                        />
                    ),
                }}
            />
            <SttTable
                data={files}
                onFetch={loadFiles}
                forceUpdateSwitch={forceUpdate}
                columns={[
                    {
                        title: t('Created'),
                        field: 'created',
                        align: 'left',
                        value: (r: FileItem) =>
                            new DateBuilder(
                                r?.created,
                                cxt?.sc?.timezone?.name,
                                cxt?.lang?.key,
                            ).dmy(),
                    },
                    {
                        title: t('Name'),
                        type: 'link',
                        field: 'name',
                        onClick: (row: FileItem) => setFile(row),
                        align: 'left',
                    },
                    {
                        align: 'right',
                        value: (row: FileItem) => {
                            if (_event.poster && _event.poster.id === row.id) {
                                return (
                                    <Box
                                        display="inline-flex"
                                        alignItems="center"
                                    >
                                        <span style={{ marginRight: 8 }}>
                                            {t('pages.events.poster')}
                                        </span>
                                        <Check style={{ fontSize: 15 }} />
                                    </Box>
                                );
                            }
                            return (
                                <a href="#" onClick={() => setPoster(row)}>
                                    {t('pages.events.selectPoster')}
                                </a>
                            );
                        },
                    },
                ]}
            />
            {file && (
                <ModalFile
                    onClose={() => setFile(undefined)}
                    item={file}
                    onSave={saveFile}
                    onDelete={deleteFile}
                    onDownload={downloadFiles}
                />
            )}
        </>
    );
}
